import { useState } from "react";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtDate } from "./formatters";

interface OverdueItem {
  id: string;
  code: string;
  title: string;
  dueDate: string | null;
  daysOverdue: number;
  priority?: string;
  assignedToName?: string | null;
  ownerName?: string | null;
  projectName?: string | null;
}

interface OverdueData {
  tasks: OverdueItem[];
  actions: OverdueItem[];
  milestones: OverdueItem[];
  summary: {
    totalOverdue: number;
    taskCount: number;
    actionCount: number;
    milestoneCount: number;
    criticalCount: number;
  };
}

function DaysPill({ days }: { days: number }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: "hsl(0,84%,95%)", color: "hsl(0,72%,45%)" }}
    >
      {days} {days === 1 ? "day" : "days"}
    </span>
  );
}

function PriorityBadge({ priority }: { priority?: string }) {
  if (!priority) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { bg: string; fg: string }> = {
    critical: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)" },
    high: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
    medium: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
    low: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
  };
  const s = map[priority.toLowerCase()] ?? map.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: s.bg, color: s.fg }}
    >
      {priority}
    </span>
  );
}

export function OverdueItemsReport({ data }: { data: OverdueData }) {
  const [tab, setTab] = useState<"tasks" | "actions" | "milestones">("tasks");
  const tabs: Array<{ key: "tasks" | "actions" | "milestones"; label: string; count: number }> = [
    { key: "tasks", label: "Tasks", count: data.summary.taskCount },
    { key: "actions", label: "Actions", count: data.summary.actionCount },
    { key: "milestones", label: "Milestones", count: data.summary.milestoneCount },
  ];

  const cols: Array<TableColumn<OverdueItem>> = [
    {
      key: "code",
      label: "#",
      width: 110,
      render: (r) => (
        <span className="font-mono text-[12px] text-muted-foreground">{r.code}</span>
      ),
    },
    {
      key: "title",
      label: "Title",
      render: (r) => <span className="font-medium text-foreground">{r.title}</span>,
    },
    {
      key: "projectName",
      label: "Project",
      render: (r) => r.projectName ?? "—",
    },
    ...(tab !== "milestones"
      ? [
          {
            key: "priority",
            label: "Priority",
            width: 100,
            render: (r: OverdueItem) => <PriorityBadge priority={r.priority} />,
          } satisfies TableColumn<OverdueItem>,
        ]
      : []),
    {
      key: "daysOverdue",
      label: "Days Overdue",
      width: 130,
      render: (r) => <DaysPill days={r.daysOverdue} />,
    },
    {
      key: "assignedToName",
      label: tab === "milestones" ? "Owner" : "Assignee",
      render: (r) => r.assignedToName ?? r.ownerName ?? "—",
    },
    {
      key: "dueDate",
      label: "Due Date",
      width: 130,
      render: (r) => fmtDate(r.dueDate),
    },
  ];

  const rows =
    tab === "tasks"
      ? data.tasks
      : tab === "actions"
      ? data.actions
      : data.milestones;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total Overdue" value={data.summary.totalOverdue} tone="bad" />
        <StatCard label="Tasks" value={data.summary.taskCount} />
        <StatCard label="Actions" value={data.summary.actionCount} />
        <StatCard label="Milestones" value={data.summary.milestoneCount} />
        <StatCard
          label="Critical Priority"
          value={data.summary.criticalCount}
          tone="bad"
        />
      </div>

      <ReportSection
        title="Overdue Items"
        action={
          <div
            className="flex items-center gap-1 rounded-md border bg-white p-1"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {tabs.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className="rounded px-3 py-1 text-[12px] transition-colors"
                  style={{
                    background: active ? "hsl(210,55%,92%)" : "transparent",
                    color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {t.label} ({t.count})
                </button>
              );
            })}
          </div>
        }
      >
        <ReportTable
          columns={cols}
          rows={rows}
          rowKey={(r) => r.id}
          empty={`No overdue ${tab} found.`}
        />
      </ReportSection>
    </div>
  );
}
